/* eslint-disable @typescript-eslint/no-explicit-any */
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import logo from "@/assets/ppm-logo.png";
import { Row } from '@tanstack/react-table';
import { format } from 'date-fns';
import { dateformat } from '@/utils/GlobalConstants';

// Helper function to convert HSL CSS variable to hex color for react-pdf
function hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

// Get theme colors from CSS variables
function getThemeColors() {
    const root = getComputedStyle(document.documentElement);
    
    const parseHSL = (varName: string): string => {
        const value = root.getPropertyValue(varName).trim();
        const parts = value.split(/[\s,]+/).map(v => parseFloat(v));
        if (parts.length >= 3) {
            return hslToHex(parts[0], parts[1], parts[2]);
        }
        return '#000000';
    };

    return {
        header: parseHSL('--table-header'),
        headerForeground: parseHSL('--table-header-foreground'),
        background: parseHSL('--background'),
        cardBackground: parseHSL('--card'),
        foreground: parseHSL('--foreground'),
        chartRed: parseHSL('--chart-red'),
        chartAmber: parseHSL('--chart-amber'),
        chartGreen: parseHSL('--chart-green'),
        tableAlternate: parseHSL('--table-alternate-row'),
        gray: parseHSL('--muted'),
    };
}

// Create styles function that takes theme colors
const createStyles = (colors: ReturnType<typeof getThemeColors>) => StyleSheet.create({
    header: {
        flexDirection: 'row',
        backgroundColor: colors.header,
        color: colors.headerForeground,
        padding: 5,
        fontWeight: 'heavy',
    },
    text: {
        fontSize: 10,
        padding: 5,
    },
    page: {
        flexDirection: 'column',
        backgroundColor: colors.background,
        padding: 10,
    },
    body: {
        flexDirection: 'row',
    },
    section: {
        flexDirection: 'column',
        width: '50%',
        paddingHorizontal: 10
    }
});

function getFontColour(title: string, column: string, value?: Date | string, status?: string, colors?: ReturnType<typeof getThemeColors>) {
    if (title === 'Upcoming Deliverables' && column === 'plannedFinish') {
        if(value && new Date(value).getTime() < new Date().getTime() && status !== 'Complete') {
            return colors?.chartRed || '#EF4444';
        }
    }

    if(title === 'Actions' && column === 'dueDate') {
        if (value && new Date(value).getTime() < new Date().getTime() && status !== 'Closed') {
            return colors?.chartRed || '#EF4444';
        }
    }

    if(title === 'Support Requests' && column === 'dueDate') {
        if (value && new Date(value).getTime() < new Date().getTime() && status === 'Open') {
            return colors?.chartRed || '#EF4444';
        }
    }

    if (column === 'rating' || column === 'status' || column === 'impact' || column === 'probability') {
        return colors?.headerForeground || '#fff';
    }

    return colors?.foreground || '#000';
}

function getBackgroundColour(title: string, columnId: string, value: string, colors: ReturnType<typeof getThemeColors>) {
    if(columnId === 'rating' || columnId === 'impact' || columnId === 'probability') {
        return getRatingColour(value, colors);
    }

    if(columnId === 'status') {
        return getStatusColor(title, value, colors);
    }

    return '';
}

function getRatingColour(rating: string, colors: ReturnType<typeof getThemeColors>) {
    switch (rating) {
        case 'High':
            return colors.chartRed;
        case 'Medium':
            return colors.chartAmber;
        case 'Low':
            return colors.chartGreen;
        default:
            return colors.tableAlternate;
    }
}

function getStatusColor(title: string, status: string, colors: ReturnType<typeof getThemeColors>) {

    if (title === 'Support Requests') {
        switch (status) {
            case "Complete":
                return colors.header;
            case "Open":
                return colors.chartAmber;
            case "Cancelled":
                return colors.gray;
            default:
                return colors.chartAmber;
        }
    } else if (title === 'Upcoming Deliverables') {
        switch (status) {
            case "Complete":
                return colors.chartGreen;
            case "Started":
                return colors.header;
            case "Cancelled":
                return colors.gray;
            default:
                return colors.header;
        }
    }
    else {
        switch (status) {
            case "Closed":
                return colors.chartGreen;
            case "Open":
                return colors.chartAmber;
            case "Cancelled":
                return colors.gray;
            default:
                return colors.chartAmber;
        }
    }
}

Font.registerHyphenationCallback((word) => {
    return [word];
});

interface TableExportProps {
    title: string,
    header_colour: string,
    rows: Row<any>[],
    alternate_colours?: boolean
}

function DataTablePDF({ title, header_colour, rows, alternate_colours }: TableExportProps) {
    const colors = getThemeColors();
    const styles = createStyles(colors);
    
    return (
        <Document title={title}>
            <Page size="A4" orientation='landscape' style={styles.page}>
                <View style={{ flexDirection: 'row', display: 'flex', width: '100%', textAlign: 'center', alignItems: 'center' }}>
                    <Image src={logo} style={{ width: "120px", marginVertical: "5px", marginHorizontal: "10px" }} />
                    <Text style={{ fontSize: 15, marginLeft: 10 }}>{title}</Text>
                </View>
                <View>
                    <View id="table" style={{ flexDirection: 'column', width: '100%' }}>
                        <View id="header-row" style={{ flexDirection: 'row', width: '100%', backgroundColor: header_colour, borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                            {rows[0].getVisibleCells().map((cell, index) => (
                                (typeof cell.column.columnDef.header === 'string') &&
                                <View id={"th" + index} key={index} style={{ flexDirection: 'column', display: 'flex', flexGrow: 1, fontSize: 12, color: colors.headerForeground, padding: 2, width: cell.column.getSize(), textAlign: 'center' }}>
                                    <Text key={index} >{typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : ''}</Text>
                                </View>
                            ))}
                        </View>
                        <View style={{ flexDirection: 'column' }}>
                            {rows.map((row, index) => (
                                <View key={index} style={{ flexDirection: 'row', width: '100%', backgroundColor: alternate_colours && index % 2 === 0 ? colors.cardBackground : colors.tableAlternate }}>
                                    {row.getVisibleCells().map((cell, index) => (
                                        (typeof cell.column.columnDef.header === 'string') &&
                                        <View wrap={false} key={index}
                                            style={{
                                                flexDirection: 'column', display: 'flex',flexGrow: 1, padding: 2, fontSize: 10,
                                                width: cell.column.getSize(), justifyContent: 'center',

                                            }}>
                                            <View style={{
                                                display: 'flex',
                                                width: '100%',
                                                textAlign: 'center',
                                                alignItems: 'center',
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                backgroundColor: getBackgroundColour(title,cell.column.columnDef.id as string,cell.getValue() as string, colors),
                                                color: getFontColour(title, String(cell.column.columnDef.id), cell.getValue() as string | Date | undefined, cell.row.getValue('status') ? cell.row.getValue('status') as string : '', colors),
                                            }}>
                                                <Text key={index} style={styles.text}>
                                                    {typeof cell.getValue() === 'string' && !isNaN(new Date(cell.getValue() as string).getTime()) && /^\d{4}-\d{2}-\d{2}/.test(cell.getValue() as string) ?
                                                        format(new Date(cell.getValue() as string), dateformat)
                                                            : (cell.getValue() === undefined || cell.getValue() === null ? "-"
                                                            : String(cell.getValue()))}</Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            ))}
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    )
}

export default DataTablePDF